import React from 'react';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormLabel,
  Box,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import QuestionLabel from './QuestionLabel';
import { COLORS } from './formTheme';
import { Controller } from 'react-hook-form';

export default function FormField({
  type = 'text',
  label,
  name,
  options = [],
  register,
  control,
  error,
  value,
  helperText,
  ariaLabel,
  ...props
}) {
  const { t } = useTranslation();
  delete props.alternateLabel;
  // validation messages are translation keys (see schema in BookingDialog)
  helperText = helperText ? t(helperText) : helperText;
  if (type === 'textarea') {
    return (
      <TextField
        label={label}
        name={name}
        {...register(name)}
        error={!!error}
        helperText={helperText}
        multiline
        rows={5}
        fullWidth
        inputProps={{ 'aria-label': ariaLabel }}
        {...props}
      />
    );
  }

  if (type === 'select') {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <FormControl fullWidth error={!!error}>
            <InputLabel id={`${name}-label`}>{label}</InputLabel>
            <Select
              {...field}
              labelId={`${name}-label`}
              label={label}
              SelectDisplayProps={{ 'aria-label': ariaLabel }}
            >
              {options.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            {helperText && (
              <Box sx={{ color: 'error.main', mt: 0.5, fontSize: '0.875rem' }}>
                {helperText}
              </Box>
            )}
          </FormControl>
        )}
      />
    );
  }

  if (type === 'radio') {
    // short choice lists (Yes/No, Public/Private) read better as a segmented control
    const segmented =
      options.length <= 3 && options.every((o) => String(o.label).length <= 16);
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <FormControl component='fieldset' error={!!error}>
            <FormLabel component='legend' sx={{ color: COLORS.text }}>
              <QuestionLabel>{label}</QuestionLabel>
            </FormLabel>
            {segmented ? (
              <ToggleButtonGroup
                exclusive
                value={field.value || null}
                onChange={(_e, v) => v !== null && field.onChange(v)}
                onBlur={field.onBlur}
                aria-label={typeof label === 'string' ? label : name}
                sx={{
                  mt: 1,
                  '& .MuiToggleButton-root': {
                    color: 'rgba(255,255,255,0.8)',
                    borderColor: error ? 'error.main' : COLORS.borderStrong,
                    textTransform: 'none',
                    fontWeight: 600,
                    minWidth: 96,
                    minHeight: 44,
                  },
                  '& .MuiToggleButton-root.Mui-selected': {
                    backgroundColor: COLORS.accent,
                    color: COLORS.onAccent,
                  },
                  '& .MuiToggleButton-root.Mui-selected:hover': {
                    backgroundColor: 'rgba(255,255,255,0.85)',
                  },
                }}
              >
                {options.map((opt) => (
                  <ToggleButton key={opt.value} value={opt.value}>
                    {opt.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            ) : (
              <RadioGroup row {...field}>
                {options.map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    value={opt.value}
                    control={<Radio />}
                    label={opt.label}
                  />
                ))}
              </RadioGroup>
            )}
            {helperText && (
              <Box sx={{ color: 'error.main', mt: 0.5, fontSize: '0.875rem' }}>
                {helperText}
              </Box>
            )}
          </FormControl>
        )}
      />
    );
  }

  if (type === 'checkbox') {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <FormControl error={!!error}>
            <FormControlLabel
              control={<Checkbox {...field} checked={!!field.value} />}
              label={<Box sx={{ color: 'text.secondary' }}>{label}</Box>}
            />
            {helperText && (
              <Box sx={{ color: 'error.main', mt: 0.5 }}>{helperText}</Box>
            )}
          </FormControl>
        )}
      />
    );
  }

  return (
    <TextField
      label={label}
      name={name}
      type={type}
      {...register(name)}
      error={!!error}
      helperText={helperText}
      fullWidth
      inputProps={{ 'aria-label': ariaLabel }}
      {...props}
    />
  );
}
